import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { Button } from '@/components/ui/Button';
import { FormGroup } from '@/components/form/FormGroup';
import { Input } from '@/components/form/Input';
import { Textarea } from '@/components/form/Textarea';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { getSettings, listSlaSteps, updateSettings } from '@/api/admin';

export function SettingsPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState({
    name: '',
    address: '',
    gstin: '',
    stateCode: '',
    mobile: '',
    email: '',
    website: '',
    linkedin: '',
    facebook: '',
    instagram: '',
    logoData: '',

    bankName: '',
    bankAccount: '',
    bankBranch: '',
    bankIfsc: '',
    termsConditions: '',
    darkMode: true,
    glassmorphism: true,
    aiAlerts: true,
    autoPrLowStock: true,
    procurementMode: 'express' as 'express' | 'full' | 'both',
    weightTolerancePct: '3',
  });

  const sQ = useQuery({ queryKey: ['settings'], queryFn: getSettings });
  const slaQ = useQuery({ queryKey: ['sla-steps'], queryFn: listSlaSteps });

  useEffect(() => {
    if (!sQ.data) return;
    setForm({
      name: sQ.data.name,
      address: sQ.data.address ?? '',
      gstin: sQ.data.gstin ?? '',
      stateCode: sQ.data.stateCode ?? '',
      mobile: sQ.data.mobile ?? '',
      email: sQ.data.email ?? '',
      website: sQ.data.website ?? '',
      linkedin: sQ.data.linkedin ?? '',
      facebook: sQ.data.facebook ?? '',
      instagram: sQ.data.instagram ?? '',
      logoData: sQ.data.logoData ?? '',
      bankName: sQ.data.bankName ?? '',
      bankAccount: sQ.data.bankAccount ?? '',
      bankBranch: sQ.data.bankBranch ?? '',
      bankIfsc: sQ.data.bankIfsc ?? '',
      termsConditions: sQ.data.termsConditions ?? '',
      darkMode: sQ.data.darkMode,
      glassmorphism: sQ.data.glassmorphism,
      aiAlerts: sQ.data.aiAlerts,
      autoPrLowStock: sQ.data.autoPrLowStock,
      procurementMode: sQ.data.procurementMode ?? 'express',
      weightTolerancePct:
        sQ.data.weightTolerancePct != null ? String(sQ.data.weightTolerancePct) : '3',
    });
  }, [sQ.data]);

  const mut = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      toast.push('Settings saved', 'success');
      qc.invalidateQueries({ queryKey: ['settings'] });
    },
    onError: (e) => {
      toast.push(apiError(e, 'Failed'), 'error');
    },
  });

  const expressOn = form.procurementMode === 'express' || form.procurementMode === 'both';
  const fullOn = form.procurementMode === 'full' || form.procurementMode === 'both';

  // Toggle one PO route on/off. At least one route must always stay enabled —
  // turning off the last one is blocked so an MRN can never lose every path to a PO.
  function toggleRoute(route: 'express' | 'full') {
    const nextExpress = route === 'express' ? !expressOn : expressOn;
    const nextFull = route === 'full' ? !fullOn : fullOn;
    if (!nextExpress && !nextFull) {
      toast.push('At least one PO route must stay enabled.', 'warning');
      return;
    }
    const next = nextExpress && nextFull ? 'both' : nextExpress ? 'express' : 'full';
    setForm({ ...form, procurementMode: next });
  }

  return (
    <div>
      <SectionHeader
        title="Settings"        actions={
          <Button variant="primary" size="sm" onClick={() => mut.mutate({ ...form, weightTolerancePct: Number(form.weightTolerancePct) || 0 })} disabled={mut.isPending}>
            {mut.isPending ? 'Saving…' : 'Save Settings'}
          </Button>
        }
      />

      <div data-compact-form="true" className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
          <h3 className="mb-4 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
            <span className="h-3.5 w-1 rounded-full bg-accent" />
            Company Profile
          </h3>
          <div className="grid grid-cols-1 gap-3">
            <FormGroup label="Company Name">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormGroup>
            <FormGroup label="Address">
              <Textarea
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
            </FormGroup>
            <div className="grid grid-cols-2 gap-3">
              <FormGroup label="GSTIN">
                <Input
                  value={form.gstin}
                  onChange={(e) => setForm({ ...form, gstin: e.target.value })}
                />
              </FormGroup>
              <FormGroup label="State Code">
                <Input
                  maxLength={2}
                  value={form.stateCode}
                  onChange={(e) => setForm({ ...form, stateCode: e.target.value })}
                />
              </FormGroup>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormGroup label="Mobile">
                <Input
                  value={form.mobile}
                  onChange={(e) => setForm({ ...form, mobile: e.target.value })}
                />
              </FormGroup>
              <FormGroup label="Email">
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </FormGroup>
              <FormGroup label="Website">
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="www.grhamoy.com" />
              </FormGroup>
              <FormGroup label="LinkedIn">
                <Input value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} placeholder="linkedin.com/company/…" />
              </FormGroup>
              <FormGroup label="Facebook">
                <Input value={form.facebook} onChange={(e) => setForm({ ...form, facebook: e.target.value })} placeholder="facebook.com/…" />
              </FormGroup>
              <FormGroup label="Instagram">
                <Input value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} placeholder="instagram.com/…" />
              </FormGroup>
            </div>
          </div>
        </section>

        {/* Right column: Bank Details, then Dispatch Quality underneath (beside Company Profile). */}
        <div className="flex flex-col gap-4">
          {/* Bank Details */}
          <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
            <h3 className="mb-4 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
              <span className="h-3.5 w-1 rounded-full bg-accent" />
              Bank Details
            </h3>
            <div className="grid grid-cols-1 gap-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormGroup label="Bank Name">
                  <Input
                    value={form.bankName}
                    onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                    placeholder="e.g. State Bank of India"
                  />
                </FormGroup>
                <FormGroup label="Account Number">
                  <Input
                    value={form.bankAccount}
                    onChange={(e) => setForm({ ...form, bankAccount: e.target.value })}
                    placeholder="e.g. 012345678901"
                  />
                </FormGroup>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FormGroup label="Branch Name">
                  <Input
                    value={form.bankBranch}
                    onChange={(e) => setForm({ ...form, bankBranch: e.target.value })}
                    placeholder="e.g. MG Road Branch"
                  />
                </FormGroup>
                <FormGroup label="IFSC Code">
                  <Input
                    value={form.bankIfsc}
                    onChange={(e) => setForm({ ...form, bankIfsc: e.target.value.toUpperCase() })}
                    placeholder="e.g. SBIN0001234"
                  />
                </FormGroup>
              </div>
            </div>
          </section>
          {/* Dispatch quality — weight-check tolerance for Outward GRN. */}
          <section className="flex-1 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
            <h3 className="mb-1 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
              <span className="h-3.5 w-1 rounded-full bg-accent" />
              Dispatch Quality
            </h3>
            <p className="mb-3 text-[11px] text-[var(--text-muted)]">
              Weight-check tolerance at Outward GRN — the allowed variance between the weighbridge
              gross (minus packaging) and the expected net from the item master.
            </p>
            <FormGroup label="Weight variance tolerance (± %)">
              <Input
                type="number"
                step="0.1"
                min={0}
                value={form.weightTolerancePct}
                onChange={(e) => setForm({ ...form, weightTolerancePct: e.target.value })}
                className="w-32"
              />
            </FormGroup>
            <div className="mt-2 text-[11px] text-[var(--text-muted)]">
              Outside ± {form.weightTolerancePct || '0'}% holds the GRN for Director / Admin override.
              <span className="ml-1 font-semibold text-[var(--text-secondary)]">Save Settings to apply.</span>
            </div>
          </section>
        </div>

        {/* Terms & Conditions */}
        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
          <h3 className="mb-4 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
            <span className="h-3.5 w-1 rounded-full bg-accent" />
            Terms &amp; Conditions
          </h3>
          <FormGroup label="Default T&amp;C printed on POs and invoices">
            <Textarea
              rows={5}
              value={form.termsConditions}
              onChange={(e) => setForm({ ...form, termsConditions: e.target.value })}
              placeholder="e.g. 80% payment in advance with PO. Delivery within 7 days..."
            />
          </FormGroup>
          <div className="mt-3 flex justify-end">
            <Button
              variant="primary"
              size="sm"
              onClick={() => mut.mutate({ ...form, weightTolerancePct: Number(form.weightTolerancePct) || 0 })}
              disabled={mut.isPending}
            >
              {mut.isPending ? 'Saving…' : 'Save T&C'}
            </Button>
          </div>
        </section>

        {/* Procurement flow — two independent route switches (Express / Full).
            Both on = coexistence; the guard keeps at least one enabled. */}
        <section className="rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
          <h3 className="mb-1 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
            <span className="h-3.5 w-1 rounded-full bg-accent" />
            Procurement Flow
          </h3>
          <p className="mb-3 text-[11px] text-[var(--text-muted)]">
            How an approved PR becomes a Purchase Order. At least one route must stay on.
          </p>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <RouteToggle
              label="Fast-track PO"
              desc="Approved PR → PO. Skips RFQ + Quote Approval."
              on={expressOn}
              onToggle={() => toggleRoute('express')}
            />
            <RouteToggle
              label="Full Approval"
              desc="PR → RFQ → Quote Approval → PO."
              on={fullOn}
              onToggle={() => toggleRoute('full')}
            />
          </div>
          <div className="mt-2.5 text-[11px] font-semibold text-[var(--text-secondary)]">
            {form.procurementMode === 'both'
              ? 'An approved PR can be fast-tracked to a PO or sent to RFQ — pick per PR.'
              : form.procurementMode === 'express'
                ? 'Only the Fast-track PO route is available.'
                : 'Only the Full Approval route is available.'}
            <span className="ml-1 font-normal text-[var(--text-muted)]">Save Settings to apply.</span>
          </div>
        </section>

        <section className="md:col-span-2 rounded-2xl border border-[var(--border-color)] bg-[var(--card-bg)] p-4 shadow-md sm:p-5">
          <h3 className="mb-4 flex items-center gap-2 text-xs font-extrabold uppercase tracking-widest text-accent">
            <span className="h-3.5 w-1 rounded-full bg-accent" />
            SLA Steps ({slaQ.data?.length ?? 0} configured)
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-[var(--bg-soft)] text-[10px] uppercase text-[var(--text-muted)]">
                <tr>
                  <th className="px-2 py-1 text-left">Step</th>
                  <th className="px-2 py-1 text-left">Category</th>
                  <th className="px-2 py-1 text-left">Responsible Role</th>
                  <th className="px-2 py-1 text-right">Hours</th>
                  <th className="px-2 py-1 text-right">Warn @</th>
                  <th className="px-2 py-1 text-left">Escalate To</th>
                  <th className="px-2 py-1 text-left">Active</th>
                </tr>
              </thead>
              <tbody>
                {(slaQ.data ?? []).map((s) => (
                  <tr key={s.id} className="border-t border-[var(--border-soft)]">
                    <td className="px-2 py-1 font-semibold">{s.name}</td>
                    <td className="px-2 py-1">{s.category}</td>
                    <td className="px-2 py-1">{s.responsibleRole}</td>
                    <td className="px-2 py-1 text-right">{s.hours}h</td>
                    <td className="px-2 py-1 text-right">{s.warnAtPct}%</td>
                    <td className="px-2 py-1">{s.escalateTo ?? '—'}</td>
                    <td className="px-2 py-1">{s.active ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function RouteToggle({
  label,
  desc,
  on,
  onToggle,
}: {
  label: string;
  desc: string;
  on: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={on}
      className={
        'flex flex-col items-start gap-0.5 rounded-lg border p-3 text-left transition ' +
        (on
          ? 'border-accent bg-accent/[0.06] shadow-sm'
          : 'border-[var(--border-soft)] bg-[var(--bg-soft)] opacity-70 hover:opacity-100')
      }
    >
      <span className="flex w-full items-center justify-between">
        <span className="text-xs font-extrabold text-[var(--text-primary)]">{label}</span>
        <span
          className={
            'rounded-full px-1.5 py-0.5 text-[9px] font-bold ' +
            (on ? 'bg-accent text-white' : 'bg-[var(--border-color)] text-[var(--text-muted)]')
          }
        >
          {on ? 'ON' : 'OFF'}
        </span>
      </span>
      <span className="text-[10px] text-[var(--text-muted)]">{desc}</span>
    </button>
  );
}
